// Full-page redirect version of the "Sign up with Google" popup.
//
// The popup (useGoogleLogin) can only open from a click; browsers block it on
// page load. For links like /sign-up?source=website&method=google we send the
// user to Google's sign-in page instead, using the same client and scopes as the
// popup, and Google sends them back to /sign-up with an access token in the URL
// hash. Requires `${origin}/sign-up` in the OAuth client's authorized redirect URIs.

const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';
const STORAGE_KEY = 'googleSignUpRedirect';

const getRedirectUri = () => `${window.location.origin}/sign-up`;

export const buildGoogleSignUpUrl = (clientId, state) => {
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: getRedirectUri(),
    response_type: 'token',
    scope: 'openid profile email',
    include_granted_scopes: 'true',
    prompt: 'select_account',
    state,
  });

  return `${GOOGLE_AUTH_URL}?${params.toString()}`;
};

// Sends the browser to Google. `returnSearch` is the query string to restore
// once Google sends the user back (Google only accepts the bare redirect URI).
export const startGoogleSignUpRedirect = (clientId, returnSearch) => {
  const state = Math.random().toString(36).slice(2) + Date.now().toString(36);

  sessionStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ state, search: returnSearch }),
  );

  // So pressing Back on Google's page doesn't bounce straight back to Google
  window.history.replaceState(
    null,
    null,
    `${window.location.pathname}${returnSearch}`,
  );
  window.location.assign(buildGoogleSignUpUrl(clientId, state));
};

// Called from app.jsx before React renders anything, so /sign-up?method=google
// goes to Google while index.html's loader is still showing instead of flashing
// the sign-up page first. Logged-in users are left alone (SignUpPage sends them
// to the dashboard). Returns true if the browser is being sent to Google.
export const maybeStartGoogleSignUpRedirect = (clientId) => {
  const params = new URLSearchParams(window.location.search);
  const path = window.location.pathname.replace(/\/$/, '');

  if (
    !clientId ||
    path !== '/sign-up' ||
    params.get('method') !== 'google' ||
    localStorage.getItem('token')
  ) {
    return false;
  }

  // method=google is dropped from the URL we come back to, so cancelling
  // shows the normal sign-up page instead of looping.
  params.delete('method');
  const returnSearch = params.toString() ? `?${params.toString()}` : '';

  startGoogleSignUpRedirect(clientId, returnSearch);
  return true;
};

// True when the URL hash looks like Google's response, so the sign-up form can
// show its spinner from the first render instead of flashing the empty form.
export const isGoogleSignUpReturn = () =>
  /(^#|&)(access_token|error)=/.test(window.location.hash);

// When the page loads after Google's redirect, returns
// { accessToken } on success, { error } if the user cancelled or Google refused,
// or null if this isn't a return from Google. Restores the original URL.
export const readGoogleSignUpRedirect = () => {
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));

  if (!hash.has('access_token') && !hash.has('error')) {
    return null;
  }

  let saved = null;
  try {
    saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
  } catch (e) {
    saved = null;
  }
  sessionStorage.removeItem(STORAGE_KEY);

  // Ignore responses we didn't ask for
  if (!saved || saved.state !== hash.get('state')) {
    window.history.replaceState(null, null, window.location.pathname);
    return null;
  }

  window.history.replaceState(
    null,
    null,
    `${window.location.pathname}${saved.search || ''}`,
  );

  if (hash.has('error')) {
    return { error: hash.get('error') };
  }

  return { accessToken: hash.get('access_token') };
};
